import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/format';
import { CURRENCIES } from '@/config/withdrawal';
import {
  generateBankLogin,
  generateBankPassword,
  generateSimulatedCard,
  formatCardNumber,
  type SimulatedCard,
} from '@/utils/withdrawal';
import Modal from './Modal';
import CredentialsModal from './CredentialsModal';
import type { AdminClient } from '@/mocks/adminClients';
import type { NewClientInput } from '@/hooks/useAdminData';

type ClientsTabProps = {
  clients: AdminClient[];
  managerEmail: string;
  onAddClient: (
    data: NewClientInput,
    manager: string,
    options?: { sendCredentials?: boolean },
  ) => Promise<AdminClient | null>;
  onToggleStatus: (clientId: string, manager: string) => void;
  onSaveCredentials: (
    clientId: string,
    login: string,
    password: string,
    manager: string,
  ) => Promise<boolean>;
  onSendCredentials: (
    clientId: string,
    recipient: string,
    manager: string,
  ) => Promise<{ ok: boolean; to?: string; error?: string }>;
};

type ClientFormState = {
  fullName: string;
  accessCode: string;
  email: string;
  balance: string;
  currency: string;
  bankLogin: string;
  bankPassword: string;
  card: SimulatedCard;
  sendCredentials: boolean;
  maxBankAttempts: string;
  maxCardAttempts: string;
};

const EMPTY_FORM: ClientFormState = {
  fullName: '',
  accessCode: '',
  email: '',
  balance: '',
  currency: 'XPF',
  bankLogin: '',
  bankPassword: '',
  card: generateSimulatedCard(),
  sendCredentials: true,
  maxBankAttempts: '2',
  maxCardAttempts: '2',
};

function nextAccessCode(clients: AdminClient[]): string {
  const numericCodes = clients
    .map((entry) => Number(entry.accessCode))
    .filter((value) => Number.isFinite(value));
  const next = numericCodes.length > 0 ? Math.max(...numericCodes) + 1 : 20001;
  return String(next);
}

/** Borne le nombre de tentatives saisies entre 1 et 10. */
function clampAttempts(value: string): number {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return Math.min(parsed, 10);
}

export default function ClientsTab({
  clients,
  managerEmail,
  onAddClient,
  onToggleStatus,
  onSaveCredentials,
  onSendCredentials,
}: ClientsTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ClientFormState>(EMPTY_FORM);
  const [loginEdited, setLoginEdited] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [credentialsFor, setCredentialsFor] = useState<AdminClient | null>(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return clients;
    return clients.filter(
      (client) =>
        client.fullName.toLowerCase().includes(query) ||
        client.accessCode.includes(query) ||
        client.email.toLowerCase().includes(query) ||
        client.bankLogin.toLowerCase().includes(query),
    );
  }, [clients, search]);

  const openForm = () => {
    const code = nextAccessCode(clients);
    setForm({
      ...EMPTY_FORM,
      accessCode: code,
      bankLogin: generateBankLogin(code),
      bankPassword: generateBankPassword(),
      card: generateSimulatedCard(),
    });
    setLoginEdited(false);
    setFormError(null);
    setShowForm(true);
  };

  const handleCodeChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      accessCode: value,
      bankLogin: loginEdited ? prev.bankLogin : generateBankLogin(value),
    }));
  };

  const handleRegenerateCard = () => {
    setForm((prev) => ({ ...prev, card: generateSimulatedCard() }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const code = form.accessCode.trim();

    if (!form.fullName.trim() || !form.bankLogin.trim() || !form.bankPassword.trim()) {
      setFormError(t('admin.clients.form.errorRequired'));
      return;
    }
    if (code && clients.some((entry) => entry.accessCode === code)) {
      setFormError(t('admin.clients.form.codeTaken'));
      return;
    }
    if (form.sendCredentials && !form.email.trim()) {
      setFormError(t('admin.clients.form.emailRequiredForSend'));
      return;
    }

    setSubmitting(true);
    const created = await onAddClient(
      {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: '',
        balance: Number(form.balance) || 0,
        currency: form.currency,
        status: 'active',
        memberSince: new Date().toISOString().slice(0, 10),
        accessCode: code,
        bankLogin: form.bankLogin.trim(),
        bankPassword: form.bankPassword.trim(),
        cardBrand: form.card.brand,
        cardNumber: form.card.number,
        cardExpiry: form.card.expiry,
        cardCvv: form.card.cvv,
        maxBankAttempts: clampAttempts(form.maxBankAttempts),
        maxCardAttempts: clampAttempts(form.maxCardAttempts),
      },
      managerEmail,
      { sendCredentials: form.sendCredentials },
    );
    setSubmitting(false);

    if (!created) {
      setFormError(t('common.error'));
      return;
    }

    setShowForm(false);
    setCreatedCode(created.accessCode);
    window.setTimeout(() => setCreatedCode(null), 8000);
  };

  const renderCredentialsStatus = (client: AdminClient) => {
    if (!client.bankLogin) {
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-foreground-100 px-2.5 py-1 text-xs font-semibold text-foreground-700">
          <span className="h-1.5 w-1.5 rounded-full bg-foreground-400"></span>
          {t('admin.credentials.notGenerated')}
        </span>
      );
    }
    return (
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
          client.credentialsSentAt
            ? 'bg-primary-100 text-primary-900'
            : 'bg-secondary-100 text-secondary-900'
        }`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            client.credentialsSentAt ? 'bg-primary-500' : 'bg-secondary-500'
          }`}
        ></span>
        {client.credentialsSentAt ? t('admin.credentials.sent') : t('admin.credentials.generated')}
      </span>
    );
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100';

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-xl font-semibold text-foreground-950">
            {t('admin.clients.title')}
          </h1>
          <p className="mt-1 text-sm text-foreground-600">{t('admin.clients.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={openForm}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-add-line"></i>
          {t('admin.clients.add')}
        </button>
      </div>

      {createdCode && (
        <div className="flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
          <i className="ri-checkbox-circle-line mt-0.5 text-lg text-primary-700"></i>
          <div>
            <p className="text-sm font-semibold text-primary-900">{t('admin.clients.created')}</p>
            <p className="mt-1 font-heading text-lg font-bold tracking-[0.3em] text-primary-800">
              {createdCode}
            </p>
          </div>
        </div>
      )}

      <div className="relative">
        <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('admin.clients.searchPh')}
          className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-5 py-3 font-semibold">{t('admin.clients.code')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.clients.name')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.clients.balance')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.clients.currency')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.clients.credentials')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.clients.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.clients.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-foreground-500">
                    {t('admin.clients.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map((client) => (
                  <tr key={client.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="px-5 py-3">
                      <span className="rounded-md bg-secondary-100 px-2 py-1 text-xs font-bold text-secondary-900">
                        {client.accessCode}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-foreground-900">{client.fullName}</p>
                      {client.email && (
                        <p className="mt-0.5 text-xs text-foreground-500">{client.email}</p>
                      )}
                    </td>
                    <td className="px-5 py-3 font-semibold text-foreground-900">
                      {formatCurrency(client.balance, client.currency)}
                    </td>
                    <td className="px-5 py-3 text-foreground-700">{client.currency}</td>
                    <td className="px-5 py-3">{renderCredentialsStatus(client)}</td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${
                          client.status === 'active'
                            ? 'bg-primary-100 text-primary-900'
                            : 'bg-foreground-100 text-foreground-700'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            client.status === 'active' ? 'bg-primary-500' : 'bg-foreground-400'
                          }`}
                        ></span>
                        {client.status === 'active'
                          ? t('admin.clients.statusActive')
                          : t('admin.clients.statusSuspended')}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCredentialsFor(client)}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-shield-keyhole-line"></i>
                          {t('admin.clients.manageCredentials')}
                        </button>
                        <button
                          type="button"
                          onClick={() => onToggleStatus(client.id, managerEmail)}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          <i
                            className={
                              client.status === 'active' ? 'ri-pause-circle-line' : 'ri-play-circle-line'
                            }
                          ></i>
                          {client.status === 'active'
                            ? t('admin.clients.suspend')
                            : t('admin.clients.reactivate')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <Modal
          title={t('admin.clients.form.title')}
          subtitle={t('admin.clients.form.subtitle')}
          onClose={() => setShowForm(false)}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  {t('admin.clients.form.fullName')}
                </label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  {t('admin.clients.form.code')}
                </label>
                <input
                  type="text"
                  value={form.accessCode}
                  onChange={(event) => handleCodeChange(event.target.value)}
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-foreground-500">{t('admin.clients.form.codeHint')}</p>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.clients.form.email')}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                placeholder={t('admin.clients.form.emailPh')}
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  {t('admin.clients.form.balance')}
                </label>
                <input
                  type="number"
                  value={form.balance}
                  onChange={(event) => setForm((prev) => ({ ...prev, balance: event.target.value }))}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  {t('admin.clients.form.currency')}
                </label>
                <select
                  value={form.currency}
                  onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value }))}
                  className={`${inputClass} cursor-pointer`}
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-md border border-background-200 bg-background-100 p-3.5">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-800">
                <i className="ri-shield-check-line text-base text-accent-700"></i>
                {t('admin.clients.form.attemptsSection')}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    {t('admin.clients.form.maxBankAttempts')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={form.maxBankAttempts}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, maxBankAttempts: event.target.value }))
                    }
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    {t('admin.clients.form.maxCardAttempts')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={form.maxCardAttempts}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, maxCardAttempts: event.target.value }))
                    }
                    className={inputClass}
                  />
                </div>
              </div>
              <p className="mt-2 text-xs text-foreground-500">
                {t('admin.clients.form.attemptsHint')}
              </p>
            </div>

            <div className="rounded-md border border-background-200 bg-background-100 p-3.5">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-800">
                <i className="ri-shield-keyhole-line text-base text-primary-700"></i>
                {t('admin.clients.form.credentialsSection')}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    {t('admin.clients.form.bankLogin')}
                  </label>
                  <input
                    type="text"
                    value={form.bankLogin}
                    onChange={(event) => {
                      setLoginEdited(true);
                      setForm((prev) => ({ ...prev, bankLogin: event.target.value }));
                    }}
                    className={`${inputClass} font-mono`}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                    {t('admin.clients.form.bankPassword')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={form.bankPassword}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, bankPassword: event.target.value }))
                      }
                      className={`${inputClass} pr-12 font-mono`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, bankPassword: generateBankPassword() }))
                      }
                      aria-label={t('admin.clients.form.regenerate')}
                      className="absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
                    >
                      <i className="ri-refresh-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-background-200 bg-background-100 p-3.5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
                  <i className="ri-bank-card-2-line text-base text-accent-700"></i>
                  {t('admin.clients.form.cardSection')}
                </p>
                <button
                  type="button"
                  onClick={handleRegenerateCard}
                  aria-label={t('admin.clients.form.cardRegenerate')}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-refresh-line"></i>
                  {t('admin.clients.form.cardRegenerate')}
                </button>
              </div>
              <div className="rounded-md border border-dashed border-accent-300 bg-accent-50 px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-accent-800">
                    {form.card.brand}
                  </span>
                  <i className="ri-bank-card-line text-lg text-accent-700"></i>
                </div>
                <p className="mt-2 font-mono text-base font-semibold tracking-widest text-foreground-950">
                  {formatCardNumber(form.card.number)}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-foreground-600">
                  <span>
                    {t('admin.credentials.cardExpiry')} :{' '}
                    <strong className="text-foreground-900">{form.card.expiry}</strong>
                  </span>
                  <span>
                    CVV : <strong className="text-foreground-900">{form.card.cvv}</strong>
                  </span>
                </div>
              </div>
              <p className="mt-2 text-xs text-foreground-500">{t('admin.clients.form.cardHint')}</p>
            </div>

            <label className="flex items-start gap-2.5 rounded-md border border-background-200 bg-background-50 px-3.5 py-3">
              <input
                type="checkbox"
                checked={form.sendCredentials}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, sendCredentials: event.target.checked }))
                }
                className="mt-0.5 h-4 w-4 cursor-pointer accent-primary-500"
              />
              <span>
                <span className="block text-sm font-semibold text-foreground-800">
                  {t('admin.clients.form.sendCredentials')}
                </span>
                <span className="mt-0.5 block text-xs text-foreground-500">
                  {t('admin.clients.form.sendCredentialsHint')}
                </span>
              </span>
            </label>

            {formError && (
              <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{formError}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                {t('admin.clients.form.cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
              >
                {submitting && <i className="ri-loader-4-line animate-spin"></i>}
                {t('admin.clients.form.submit')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {credentialsFor && (
        <CredentialsModal
          client={credentialsFor}
          managerEmail={managerEmail}
          onSave={onSaveCredentials}
          onSend={onSendCredentials}
          onClose={() => setCredentialsFor(null)}
        />
      )}
    </div>
  );
}